import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { switchMap, tap, takeUntil } from 'rxjs/operators';

import { BaseComponent } from '../../base.component';
import { Account } from '../../shared/models';
import { Features } from '../../shared/models/features.enum';
import { SessionService } from '../../shared/session.service';
import { TokenExchangeService } from '../../shared/token-exchange.service';
import { AccountService } from '../account.service';

@Component({
  selector: 'app-preload',
  templateUrl: './preload.component.html',
  styleUrls: ['./preload.component.css']
})
export class PreloadComponent extends BaseComponent implements OnInit {
  private defaultAccountId = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sessionService: SessionService,
    private tokenExchangeService: TokenExchangeService,
    private accountService: AccountService
  ) {
    super();
  }

  ngOnInit() {
    this.tokenExchangeService.exchange()
      .pipe(
        switchMap(() => this.getDefaultAccountId()),
        switchMap(() => {
          if (!this.defaultAccountId) {
            throw new Error('No default account found');
          }
          return this.accountService.account(this.defaultAccountId);
        }),
        takeUntil(this.unsubscribe)
      )
      .subscribe({
        next: (account) => {
          if (!account) {
            return;
          }

          let isCsr = false;
          this.sessionService.isCsr.pipe(takeUntil(this.unsubscribe)).subscribe(v => isCsr = v);

          if (isCsr) {
            for (let i = 0; i < account.previousFeatures.length; i++) {
              if (!account.features.includes(account.previousFeatures[i])) {
                account.features.push(account.previousFeatures[i]);
              }
            }
          }

          this.sessionService.setAccount(account);

          const optionalPath = this.route.snapshot.queryParams['path'];
          let defaultPath = this.getDefaultPath(account);
          defaultPath = this.sessionService.redirectUrl || defaultPath;
          this.sessionService.clearRedirectUrl();
          this.router.navigateByUrl(optionalPath || defaultPath);
        },
        error: () => {
          this.router.navigateByUrl('log-out');
        }
      });
  }

  private getDefaultAccountId() {
    const yToken = this.route.snapshot.queryParams['y'];
    const tToken = this.route.snapshot.queryParams['t'];

    if (tToken) {
      this.sessionService.setIsCsr(true);
      return this.accountService.defaultImpersonation(tToken).pipe(
        tap(id => {
          this.defaultAccountId = id;
        })
      );
    }

    if (yToken) {
      return this.accountService.default(yToken).pipe(
        tap(id => {
          this.defaultAccountId = id;
        })
      );
    }

    return this.accountService.getFirst().pipe(
      tap(id => {
        this.defaultAccountId = id;
      })
    );
  }

  private getDefaultPath(account: Account): string {
    if (account.hasFeature(Features.SEO) || account.hasFeature(Features.PageOptimization) || account.hasFeature(Features.StarterSEO)) {
      return '/seo/dashboard';
    }
    if (account.hasFeature(Features.BusinessCitations)) {
      return '/business-citations/local-listings';
    }
    if (account.hasFeature(Features.StandaloneBacklink)) {
      return '/seo/work-history';
    }
    if (!account.features.length) {
      return '/no-products';
    }
    return '/seo/dashboard';
  }
}
